import React, { useState } from 'react';
// If you use Font Awesome with the Pro icons, adjust imports accordingly

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope, faComment, faCalendar, faArrowRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import ContactModal from '../modals/contact-modal';
import RequestCallbackModal from '../modals/request-callback-modal';

// If you use other icons or the Pro set, just replace the imports

/**
 * CommunicationOptions
 *
 * A responsive, stylish component that renders multiple communication-channel buttons 
 * (Phone, Live Chat, Email, WhatsApp, Callback) based on the provided `channels` object. 
 *
 * Props:
 *  - channels: object 
 *      An object with channel identifiers as keys and their values
 *  - onPhoneClick(): function
 *      Handler when user clicks "Call"
 *  - onLiveChatClick(): function
 *      Handler when user clicks "Live Chat"
 *  - onEmailClick(): function
 *      Handler when user clicks "Email"
 *  - onWhatsAppClick(): function
 *      Handler when user clicks "WhatsApp"
 *  - onCallbackSubmit(): function
 *      Handler when user submits callback form
 *  - headerText?: string
 *      Optional heading displayed above the buttons
 *  - subText?: string
 *      Optional short description displayed before the buttons
 *
 * Example usage:
 *  <CommunicationOptions
 *    channels={["live_agent", "phone", "email", "whatsapp"]}
 *    onPhoneClick={() => window.location.href = "tel:+123456789"}
 *    onLiveChatClick={() => console.log("Open live chat modal")}
 *    onEmailClick={() => window.location.href = "mailto:support@example.com"}
 *    onWhatsAppClick={() => window.open("https://wa.me/123456789")}
 *  />
 */
const CommunicationOptions = ({
    channels = {},
    headerText = "We're here to help!",
    subText = "Choose how you'd like to contact us:",
}) => {
    const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);

    // Handle both object and array formats for channels
    const hasChannel = (channel) => {
        if (!channels) return false;

        // If channels is an array (e.g., ["phone", "email"])
        if (Array.isArray(channels)) {
            console.log("channels is an array", channels);
            return channels.includes(channel);
        }

        // If channels is an object (e.g., {phone: "+123456", email: "example@test.com"})
        return !!channels[channel];
    };

    // Get value for a specific channel
    const getChannelValue = (channel) => {
        if (!channels) return null;

        if (Array.isArray(channels)) {
            return null; // No values in array format
        }

        return channels[channel];
    };

    const onPhoneClick = () => {
        const phoneNumber = getChannelValue("phone");
        if (phoneNumber) {
            window.location.href = `tel:${phoneNumber}`;
        } else {
            console.log("Phone clicked, but no number provided");
        }
    };

    const onLiveChatClick = () => {
        const chatLink = getChannelValue("live_agent");
        if (chatLink) {
            window.open(chatLink, "_blank");
        } else {
            console.log("Live chat clicked");
        }
    };

    const onEmailClick = () => {
        const emailAddress = getChannelValue("email");
        if (emailAddress) {
            window.location.href = `mailto:${emailAddress}`;
        } else {
            console.log("Email clicked, but no address provided");
        }
    };

    const onWhatsAppClick = () => {
        const whatsappNumber = getChannelValue("whatsapp");
        if (whatsappNumber) {
            console.log("WhatsApp clicked, number:", whatsappNumber);
            // Remove any non-digit characters for WhatsApp format
            const cleanNumber = whatsappNumber.whatsappNumber.replace(/\D/g, '');
            console.log("WhatsApp clicked, clean number:", cleanNumber);
            window.open(`https://wa.me/${cleanNumber}`, "_blank");
        } else {
            console.log("WhatsApp clicked, but no number provided");
        }
    };

    const onBookMeetingClick = () => {
        const bookingUrl = getChannelValue("book_meeting");
        if (bookingUrl) {
            window.open(bookingUrl, "_blank");
        } else {
            console.log("Book meeting clicked, but no URL provided");
        }
    };

    const onCallbackClick = () => {
        setIsCallbackModalOpen(true);
    };

    const onContactClick = () => {
        setIsContactModalOpen(true);
    };

    

    // Icon style mapping
    const iconStyles = {
        phone: "text-blue-600",
        email: "text-purple-600",
        live_agent: "text-green-600",
        whatsapp: "text-green-600",
        book_meeting: "text-orange-600",
        callback: "text-blue-700"
    };

    return (
        <div className="my-3 mx-1 p-3 sm:p-4 bg-white shadow-lg rounded-lg w-full max-w-full sm:max-w-md border border-neutral-200">
            <h2 className="text-base sm:text-lg font-bold mb-1 sm:mb-2">{headerText}</h2>
            <p className="text-sm sm:text-base mb-3 sm:mb-4">{subText}</p>

            <div className="grid grid-cols-1 gap-2 sm:gap-4">
                {hasChannel("phone") && (
                    <button
                        onClick={onPhoneClick}
                        className="flex items-center justify-center space-x-2 rounded-lg border border-blue-500 text-blue-600 py-2 px-3 sm:px-4 text-sm sm:text-base hover:bg-blue-50 active:bg-blue-100 transition"
                    >
                        <FontAwesomeIcon icon={faPhone} className={`${iconStyles.phone} mr-2`} />
                        <span>{channels.phone.buttonText || "Call us"}</span>
                    </button>
                )}

                {hasChannel("email") && (
                    <button
                        onClick={onContactClick}
                        className="flex items-center justify-center space-x-2 rounded-lg border border-purple-500 text-purple-600 py-2 px-3 sm:px-4 text-sm sm:text-base hover:bg-purple-50 active:bg-purple-100 transition"
                    >
                        <FontAwesomeIcon icon={faEnvelope} className={`${iconStyles.email} mr-2`} />
                        <span>{channels.email.buttonText || "Skicka e-post"}</span>
                    </button>
                )}

                {hasChannel("whatsapp") && (
                    <button
                        onClick={onWhatsAppClick}
                        className="flex items-center justify-center space-x-2 rounded-lg border border-green-500 text-green-600 py-2 px-3 sm:px-4 text-sm sm:text-base hover:bg-green-50 active:bg-green-100 transition"
                    >
                        <FontAwesomeIcon icon={faWhatsapp} className={`${iconStyles.whatsapp} mr-2`} />
                        <span>{channels.whatsapp.buttonText || "Kontakta via WhatsApp"}</span>
                    </button>
                )}

                {hasChannel("live_agent") && (
                    <button
                        onClick={onLiveChatClick}
                        className="flex items-center justify-center space-x-2 rounded-lg border border-green-500 text-green-600 py-2 px-3 sm:px-4 text-sm sm:text-base hover:bg-green-50 active:bg-green-100 transition"
                    >
                        <FontAwesomeIcon icon={faComment} className={`${iconStyles.live_agent} mr-2`} />
                        <span>{channels.live_agent.buttonText || "Live Chat"}</span>
                    </button>
                )}

                {hasChannel("book_meeting") && (
                    <button
                        onClick={onBookMeetingClick}
                        className="flex items-center justify-center space-x-2 rounded-lg border border-orange-500 text-orange-600 py-2 px-3 sm:px-4 text-sm sm:text-base hover:bg-orange-50 active:bg-orange-100 transition"
                    >
                        <FontAwesomeIcon icon={faCalendar} className={`${iconStyles.book_meeting} mr-2`} />
                        <span>{channels.book_meeting.buttonText || "Book Meeting"}</span>
                    </button>
                )}

                {hasChannel("callback") && (
                    <button
                        onClick={onCallbackClick}
                        className="flex items-center justify-center space-x-2 rounded-lg border border-blue-700 text-blue-700 py-2 px-3 sm:px-4 text-sm sm:text-base hover:bg-blue-50 active:bg-blue-100 transition"
                    >
                        <FontAwesomeIcon icon={faArrowRotateLeft} className={`${iconStyles.callback} mr-2`} />
                        <span>{channels.callback.buttonText || "Begär återuppringning"}</span>
                    </button>
                )}
            </div>

            {hasChannel("callback") && (
                <RequestCallbackModal
                    isOpen={isCallbackModalOpen}
                    onClose={() => setIsCallbackModalOpen(false)}
                    callbackEmail={channels.callback?.callbackUrl}
                    data={channels.callback?.modal}
                />
            )}

            {hasChannel("email") && isContactModalOpen && (
                <ContactModal
                    isOpen={isContactModalOpen}
                    onClose={() => setIsContactModalOpen(false)}
                    contactEmail={channels.email?.emailAddress}
                    data={channels.email?.modal}
                />
            )}
        </div>
    );
};

export default CommunicationOptions;
